import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../../../reduxService';
import { FormikErrors } from 'formik';

export type SubmitSource = 'StepperRun' | 'ButtonSave' | 'ButtonExecute' | 'StepperGeoGridRun' | 'ExecuteSync' | 'Unknown';
interface RequestSubmitPayload {
    source?: SubmitSource;
}

interface SubmitResult {
    success: boolean;
    data?: unknown;
    error?: unknown;
    source: SubmitSource;
}

export interface SaveNotification {
    show: boolean;
    type: 'success' | 'error';
    message: string;
}

export interface FormControlState {
    submitTrigger: number; // Un contatore o timestamp incrementale
    isSubmitting: boolean; // Opzionale, per mostrare un loader o disabilitare controlli
    lastSubmitResult: SubmitResult | null;
    submissionErrors: FormikErrors<Record<string, unknown>> | null;
    accordionToOpen: string | null; // ID dell'accordion da aprire in caso di errore
    lastRequestSource: SubmitSource;
    saveNotification: SaveNotification | null; // Snackbar notification state
}

const initialState: FormControlState = {
    submitTrigger: 0, // 0 significa nessuna richiesta attiva
    isSubmitting: false,
    lastSubmitResult: null,
    submissionErrors: null,
    accordionToOpen: null, // Inizializzato a null
    lastRequestSource: 'Unknown',
    saveNotification: null, // No notification initially
};

export const formControlSlice = createSlice({
    name: 'formControl',
    initialState,
    reducers: {
        requestSubmit: (state, action: PayloadAction<RequestSubmitPayload | undefined>) => {
            state.submitTrigger = Date.now();
            state.isSubmitting = true;
            state.lastSubmitResult = null;
            state.submissionErrors = null;
            state.accordionToOpen = null;
            // --- Memorizza la sorgente ---
            state.lastRequestSource = action.payload?.source || 'Unknown';
        },

        // Tags a submit started by calling Formik directly: no submitTrigger, so useFormSubmission does not submit again.
        markSubmitSource: (state, action: PayloadAction<SubmitSource>) => {
            state.lastRequestSource = action.payload;
        },

        // Drops the tag when no submit result consumed it, unless another requestSubmit replaced it meanwhile.
        releaseSubmitSource: (state, action: PayloadAction<SubmitSource>) => {
            if (state.lastRequestSource === action.payload) state.lastRequestSource = 'Unknown';
        },

        submitSucceeded: (state, action: PayloadAction<unknown>) => {
            // Payload torna semplice (solo dati)
            state.isSubmitting = false;
            state.lastSubmitResult = {
                success: true,
                data: action.payload,
                source: state.lastRequestSource, // <-- Usa la sorgente memorizzata
            };
            state.submissionErrors = null;
            state.accordionToOpen = null;
            state.lastRequestSource = 'Unknown'; // Resetta source dopo averla usata
        },

        submitFailed: (state, action: PayloadAction<unknown>) => {
            // Payload torna semplice (solo errore)
            state.isSubmitting = false;
            state.lastSubmitResult = {
                success: false,
                error: action.payload,
                source: state.lastRequestSource, // <-- Usa la sorgente memorizzata
            };

            const payloadWithValidationErrors = action.payload as { validationErrors?: FormikErrors<Record<string, unknown>> } | null | undefined;
            state.submissionErrors = payloadWithValidationErrors?.validationErrors ?? null;
            state.lastRequestSource = 'Unknown'; // Resetta source dopo averla usata
        },

        // Nuova azione per resettare solo il risultato
        clearLastSubmitResult: state => {
            state.lastSubmitResult = null;
        },

        // Nuova azione per impostare l'accordion da aprire
        setAccordionToOpen: (state, action: PayloadAction<string | null>) => {
            state.accordionToOpen = action.payload;
        },

        // Manteniamo le azioni esistenti per retrocompatibilità (ma aggiornate)
        triggerSubmit: state => {
            state.submitTrigger = Date.now();
            state.isSubmitting = true;
            state.lastSubmitResult = null;
            state.submissionErrors = null; // Resetta errori precedenti
            state.accordionToOpen = null; // Resetta accordion
        },

        setSubmitting: (state, action: PayloadAction<boolean>) => {
            state.isSubmitting = action.payload;
            if (!action.payload) {
                // Se si imposta isSubmitting a false, potrebbe indicare fine operazione
                // Ma non resettiamo accordionToOpen qui per flessibilità
            }
        },

        setSubmitResult: (state, action: PayloadAction<SubmitResult>) => {
            state.isSubmitting = false;
            state.lastSubmitResult = action.payload;
            if (action.payload.success) {
                state.submissionErrors = null;
                state.accordionToOpen = null; // Resetta su successo
            } else {
                const errorWithValidationErrors = action.payload.error as { validationErrors?: FormikErrors<Record<string, unknown>> } | null | undefined;
                state.submissionErrors = errorWithValidationErrors?.validationErrors ?? null;
            }
        },

        resetFormControl: state => {
            state.submitTrigger = 0;
            state.isSubmitting = false;
            state.lastSubmitResult = null;
            state.submissionErrors = null;
            state.accordionToOpen = null; // Resetta anche l'accordion
            state.lastRequestSource = 'Unknown';
        },
        clearSubmissionErrors: state => {
            state.submissionErrors = null;
            // Decidi se resettare anche accordionToOpen qui, potrebbe dipendere dal caso d'uso
            // state.accordionToOpen = null;
        },

        // Save notification actions for Snackbar
        setSaveNotification: (state, action: PayloadAction<SaveNotification>) => {
            state.saveNotification = action.payload;
        },
        clearSaveNotification: state => {
            state.saveNotification = null;
        },
    },
});

// Esporta le nuove azioni insieme a quelle esistenti
export const {
    requestSubmit,
    markSubmitSource,
    releaseSubmitSource,
    submitSucceeded,
    submitFailed,
    clearSubmissionErrors,
    clearLastSubmitResult,
    setAccordionToOpen, // Esporta la nuova azione
    triggerSubmit,
    setSubmitting,
    setSubmitResult,
    resetFormControl,
    setSaveNotification,
    clearSaveNotification,
} = formControlSlice.actions;

// Selettori
export const selectSubmitTrigger = (state: RootState) => state.formControl.submitTrigger;
export const selectIsSubmitting = (state: RootState) => state.formControl.isSubmitting;
export const selectLastSubmitResult = (state: RootState) => state.formControl.lastSubmitResult;
export const selectSubmissionErrors = (state: RootState) => state.formControl.submissionErrors;
export const selectAccordionToOpen = (state: RootState) => state.formControl.accordionToOpen; // Esporta il nuovo selettore
export const selectSaveNotification = (state: RootState) => state.formControl.saveNotification;

export default formControlSlice.reducer;
